import { get, post } from "aws-amplify/api";

import {
  DatasetUploadLink,
  POLL_INTERVAL_MS,
  errorMessageOf,
  formatBytes,
  idTokenOrThrow,
  sendDatasetUploadFile,
} from "./datasetUploadApi";

// Uploading a forecast into a ProcessedModel V2 output (docs/architecture/EXTERNAL_FORECASTS.md): the twin of
// datasetUploadApi.ts. Ask erodeapi for an upload link, POST the file to S3, then poll the upload's status while
// s3Triggerfunc checks the file and writes it as the output's forecast for its issue. The browser does no parsing
// of its own; the trigger is the one validator. The reference client is scripts/erodeForecastUpload/.

export type ForecastUploadStatus = "REQUESTED" | "RECEIVED" | "LOADED" | "REJECTED" | "FAILED" | "EXPIRED";

export interface ForecastUploadRow {
  id: string;
  outputParamId: string;
  schemaId: string;
  status: ForecastUploadStatus;
  source: "API" | "WEB" | null;
  uploadedBy: string | null;
  uploadedByEmail: string | null;
  fileName: string | null;
  /** The issue time declared when the link was requested (else the file's forecast_issued row, else the file name) */
  issueTime: string | null;
  /** Where the original is kept, as sent: `{outputLocation}/raw/{uploadId}.csv` */
  rawKey: string | null;
  /** LOADED: the `{issue}.csv` under the output the ForecastRecord points at */
  finalKey: string | null;
  sizeBytes: number | null;
  /** The issue time the forecast was filed under */
  forecastIssued: string | null;
  rowCount: number | null;
  timeRange: { startDate?: string; endDate?: string } | null;
  /** What the file declared: each parameter's unit, name and series kinds */
  parameters: Record<string, { unit?: string; name?: string; description?: string; columns?: string[] }> | null;
  validationErrors: string[] | null;
  warnings: string[] | null;
  failureReason: string | null;
  forecastRecordId: string | null;
  requestedAt: string;
  receivedAt: string | null;
  processedAt: string | null;
}

export type ForecastUploadLink = DatasetUploadLink;

/** What the upload modal and the Uploads tab need of an output param */
export interface ForecastOutputLike {
  id: string;
  name?: string | null;
  unit?: string | null;
  storageMode?: string | null;
  active?: boolean | null;
}

/** What they need of a model (a ProcessedModelSchema row, with its outputs as the list query or the store shapes them) */
export interface ForecastModelLike {
  id: string;
  name?: string | null;
  schemaType?: string | null;
  organizationId?: string | null;
  outputs?: { items?: ForecastOutputLike[] | null } | ForecastOutputLike[] | null;
}

/** The model's forecast outputs: a DATASET output (a historical model's) takes no forecast files (the API answers 400) */
export const forecastOutputsOf = (schema: ForecastModelLike | null | undefined): ForecastOutputLike[] => {
  const raw = Array.isArray(schema?.outputs) ? schema.outputs : Array.isArray(schema?.outputs?.items) ? schema.outputs.items : [];
  return raw.filter((o) => o && o.id && (!o.storageMode || o.storageMode === "FILES") && o.active !== false);
};

export interface ForecastUploadRequest {
  fileName: string;
  /** ISO 8601 UTC instant; optional when the file carries forecast_issued or its name is dated */
  issueTime?: string | null;
}

export type ForecastUploadStage = "requesting" | "uploading" | "processing";

export interface RunForecastUploadOptions extends ForecastUploadRequest {
  onStage?: (stage: ForecastUploadStage) => void;
  onLinkIssued?: (link: ForecastUploadLink) => void;
  onUploadProgress?: (sent: number, total: number) => void;
  /** Give up waiting (the check continues server-side) after this long; default 2 minutes */
  waitTimeoutMs?: number;
  signal?: AbortSignal;
}

export interface RunForecastUploadResult {
  upload: ForecastUploadRow;
  /** True when the status was still not final at waitTimeoutMs: the file was sent and the check is still running */
  timedOut: boolean;
  readError?: string;
}

export const FORECAST_UPLOAD_ALLOWED_EXTENSIONS = [".csv"];
// Mirrors MAX_UPLOAD_BYTES in amplify/function/erodeapifunction/src/processedOutputUploads.js: a forecast is one
// issue, not a history, and the check runs inside s3Triggerfunc. The link's own maxBytes is the authority.
export const FORECAST_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
const DEFAULT_WAIT_TIMEOUT_MS = 2 * 60 * 1000;

export const isFinalForecastUploadStatus = (status: ForecastUploadStatus | null | undefined): boolean =>
  status === "LOADED" || status === "REJECTED" || status === "FAILED" || status === "EXPIRED";

export const FORECAST_UPLOAD_STATUS_LABELS: Record<ForecastUploadStatus, string> = {
  REQUESTED: "Waiting for file",
  RECEIVED: "Checking",
  LOADED: "Loaded",
  REJECTED: "Rejected",
  FAILED: "Failed",
  EXPIRED: "Not sent",
};

/** Extension and size are the only checks worth doing before asking for a link; the trigger checks the content. */
export const forecastUploadFileError = (file: File | null | undefined, maxBytes: number = FORECAST_UPLOAD_MAX_BYTES): string | null => {
  if (!file) return "Choose a file to upload.";
  if (!file.name.toLowerCase().endsWith(".csv")) return "Only .csv files (a Standard time-series CSV) can be uploaded.";
  if (file.size < 1) return "The file is empty.";
  if (file.size > maxBytes) return `The file is larger than the ${formatBytes(maxBytes)} limit.`;
  return null;
};

const outputPath = (outputParamId: string, suffix: string) =>
  `/api/processed-output/${encodeURIComponent(outputParamId)}/uploads${suffix}`;

/** Step 1: an upload link plus a REQUESTED row, issued by erodeapi after its access check. */
export const requestForecastUpload = async (outputParamId: string, request: ForecastUploadRequest): Promise<ForecastUploadLink> => {
  const idToken = await idTokenOrThrow();
  const body: Record<string, string> = { fileName: request.fileName, source: "WEB" };
  if (request.issueTime) body.issueTime = request.issueTime;
  try {
    const operation = post({
      apiName: "erodeapi",
      path: outputPath(outputParamId, ""),
      options: { headers: { Authorization: `Bearer ${idToken}` }, body, retryStrategy: { strategy: "no-retry" } },
    });
    const response = await operation.response;
    const payload = (await response.body.json()) as unknown as { body?: ForecastUploadLink };
    if (!payload?.body?.url || !payload.body.uploadId) throw new Error("Unexpected response when requesting the upload link.");
    return payload.body;
  } catch (error) {
    throw new Error(errorMessageOf(error, "Could not request an upload link."));
  }
};

/** Step 2 is the same presigned POST as a dataset upload. */
export const sendForecastUploadFile = sendDatasetUploadFile;

/** Step 3: one upload's row, with EXPIRED derived by the API for a link that lapsed unused. */
export const fetchForecastUpload = async (outputParamId: string, uploadId: string): Promise<ForecastUploadRow> => {
  const idToken = await idTokenOrThrow();
  try {
    const operation = get({
      apiName: "erodeapi",
      path: outputPath(outputParamId, `/${encodeURIComponent(uploadId)}`),
      options: { headers: { Authorization: `Bearer ${idToken}` }, retryStrategy: { strategy: "no-retry" } },
    });
    const response = await operation.response;
    const payload = (await response.body.json()) as unknown as { body?: { upload?: ForecastUploadRow } };
    if (!payload?.body?.upload) throw new Error("Unexpected response when reading the upload's status.");
    return payload.body.upload;
  } catch (error) {
    throw new Error(errorMessageOf(error, "Could not read the upload's status."));
  }
};

export interface ForecastUploadPage {
  uploads: ForecastUploadRow[];
  nextToken: string | null;
}

/** The output's upload history, newest first. `status` filters on the status the API reports (EXPIRED included). */
export const listForecastUploads = async (
  outputParamId: string,
  options: { limit?: number; nextToken?: string | null; status?: ForecastUploadStatus | null } = {}
): Promise<ForecastUploadPage> => {
  const idToken = await idTokenOrThrow();
  const queryParams: Record<string, string> = {};
  if (options.limit) queryParams.limit = String(options.limit);
  if (options.status) queryParams.status = options.status;
  if (options.nextToken) queryParams.nextToken = options.nextToken;
  try {
    const operation = get({
      apiName: "erodeapi",
      path: outputPath(outputParamId, ""),
      options: { headers: { Authorization: `Bearer ${idToken}` }, queryParams, retryStrategy: { strategy: "no-retry" } },
    });
    const response = await operation.response;
    const payload = (await response.body.json()) as unknown as { body?: ForecastUploadPage };
    if (!payload?.body?.uploads) throw new Error("Unexpected response when listing uploads.");
    return { uploads: payload.body.uploads, nextToken: payload.body.nextToken || null };
  } catch (error) {
    throw new Error(errorMessageOf(error, "Could not load the upload history."));
  }
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("Upload cancelled."));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error("Upload cancelled."));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/** What the modal shows for an upload S3 accepted but whose row could not be read back: sent, outcome pending */
export const sentForecastUploadPlaceholder = (link: ForecastUploadLink, file: File, outputParamId: string): ForecastUploadRow => ({
  id: link.uploadId,
  outputParamId,
  schemaId: "",
  status: "RECEIVED",
  source: "WEB",
  uploadedBy: null,
  uploadedByEmail: null,
  fileName: file.name,
  issueTime: null,
  rawKey: null,
  finalKey: null,
  sizeBytes: file.size,
  forecastIssued: null,
  rowCount: null,
  timeRange: null,
  parameters: null,
  validationErrors: null,
  warnings: null,
  failureReason: null,
  forecastRecordId: null,
  requestedAt: new Date().toISOString(),
  receivedAt: null,
  processedAt: null,
});

/** Poll until the trigger has finished with the upload, or the wait runs out. */
export const waitForForecastUpload = async (
  outputParamId: string,
  uploadId: string,
  options: { timeoutMs?: number; intervalMs?: number; signal?: AbortSignal } = {}
): Promise<RunForecastUploadResult> => {
  const timeoutMs = options.timeoutMs ?? DEFAULT_WAIT_TIMEOUT_MS;
  const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;
  const deadline = Date.now() + timeoutMs;
  let last: ForecastUploadRow | null = null;
  let failedReads = 0;
  for (;;) {
    if (options.signal?.aborted) throw new Error("Upload cancelled.");
    try {
      last = await fetchForecastUpload(outputParamId, uploadId);
      failedReads = 0;
    } catch (error) {
      failedReads += 1;
      if (failedReads >= 3) throw error;
    }
    if (last && isFinalForecastUploadStatus(last.status)) return { upload: last, timedOut: false };
    if (Date.now() >= deadline) {
      if (!last) throw new Error("The upload's status could not be read.");
      return { upload: last, timedOut: true };
    }
    await sleep(intervalMs, options.signal);
  }
};

/** The whole flow: link, file, wait. Throws before the file is sent; after that, returns what is known. */
export const runForecastUpload = async (outputParamId: string, file: File, options: RunForecastUploadOptions): Promise<RunForecastUploadResult> => {
  const problem = forecastUploadFileError(file);
  if (problem) throw new Error(problem);
  options.onStage?.("requesting");
  const link = await requestForecastUpload(outputParamId, { fileName: options.fileName, issueTime: options.issueTime });
  options.onLinkIssued?.(link);
  if (file.size > link.maxBytes) throw new Error(`The file is larger than the ${formatBytes(link.maxBytes)} limit.`);
  options.onStage?.("uploading");
  await sendForecastUploadFile(link, file, options.onUploadProgress, options.signal);
  options.onStage?.("processing");
  try {
    return await waitForForecastUpload(outputParamId, link.uploadId, { timeoutMs: options.waitTimeoutMs, signal: options.signal });
  } catch (error: any) {
    if (options.signal?.aborted) throw error;
    return { upload: sentForecastUploadPlaceholder(link, file, outputParamId), timedOut: true, readError: error?.message };
  }
};

/** One line for a finished upload, for the modal's result box and the uploads list. */
export const describeForecastUpload = (upload: ForecastUploadRow): string => {
  switch (upload.status) {
    case "LOADED": {
      const rows = upload.rowCount != null ? `${upload.rowCount} rows` : "loaded";
      const issue = upload.forecastIssued ? `, issued ${formatIssued(upload.forecastIssued)}` : "";
      const params = upload.parameters ? Object.keys(upload.parameters) : [];
      return `${rows}${issue}${params.length ? ` (${params.join(", ")})` : ""}`;
    }
    case "REJECTED":
      return upload.validationErrors?.length ? upload.validationErrors.join("; ") : "The file was rejected.";
    case "FAILED":
      return upload.failureReason || "The platform could not process the file.";
    case "EXPIRED":
      return "The upload link lapsed before a file arrived.";
    case "RECEIVED":
      return "The file arrived and is being checked.";
    default:
      return "Waiting for the file.";
  }
};

export const formatIssued = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  return d.toISOString().replace("T", " ").replace(/:\d{2}\.\d{3}Z$/, " UTC");
};

export const forecastUploadEventProperties = (
  schema: ForecastModelLike,
  outputParamId: string,
  file: File | null | undefined,
  { result }: { result?: RunForecastUploadResult }
): Record<string, unknown> => {
  const upload = result?.upload;
  return {
    model_id: schema.id,
    template_id: schema.schemaType || null,
    output_param_id: outputParamId,
    owner_type: schema.organizationId ? "organization" : "personal",
    source: "WEB",
    status: !result ? "ERROR" : result.timedOut ? "PROCESSING" : upload?.status || "UNKNOWN",
    row_count: upload?.rowCount ?? null,
    size_bytes: file?.size ?? null,
    warning_count: upload?.warnings?.length || 0,
    rejection_count: upload?.validationErrors?.length || 0,
  };
};
